// Shipping panel for an order (OrderDetail, INTERNAL_STAFF only). CS sets the destination, chooses
// the incoterm (rest-of-world only), gets a quote, confirms/overrides the cost, and reconciles the
// actual cost + tracking from ShipHero after fulfillment. Reps/clients never render this.
import { useEffect, useState } from 'react';
import { fmtMoney } from '../lib/matrix.js';
import {
  fetchOrderShipping,
  resolveShipping,
  setDestination,
  quoteShipping,
  confirmShipping,
  reconcileShipping,
} from '../api/shipping.js';

const STATE = {
  PENDING: { label: 'Pending', cls: 'bg-surface-raised text-text-secondary' },
  QUOTED: { label: 'Quoted', cls: 'bg-accent-light text-text-primary' },
  CONFIRMED: { label: 'Confirmed', cls: 'bg-success-light text-success' },
};
const errMsg = (e) => e.response?.data?.error?.message || 'Something went wrong.';

export default function ShippingPanel({ orderId, onAutoApproved, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [country, setCountry] = useState('');
  const [cost, setCost] = useState('');
  const [duties, setDuties] = useState('');
  const [taxes, setTaxes] = useState('');

  const apply = (d) => {
    setData(d);
    setCountry(d.destinationCountry || '');
    setCost(d.shippingCost ?? '');
    setDuties(d.dutiesAmount ?? '');
    setTaxes(d.taxesAmount ?? '');
  };

  const load = async () => {
    try {
      let d = await fetchOrderShipping(orderId);
      if (!d.lane) d = await resolveShipping(orderId); // compute defaults once
      apply(d);
    } catch (e) {
      setError(errMsg(e));
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      const d = await fn();
      apply(d);
      if (d?.autoApproved) onAutoApproved?.(); // trusted client → approved on shipping confirm
      else onChanged?.(); // totals / approval readiness changed — let the order page refresh
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy('');
    }
  };

  if (!data) {
    return (
      <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-text-primary">Shipping</h3>
        {error ? <p className="mt-2 text-xs text-error">{error}</p> : <p className="mt-2 text-xs text-text-muted">Loading…</p>}
      </div>
    );
  }

  const st = STATE[data.state] || STATE.PENDING;
  const isDDP = data.incoterm === 'DDP';
  const estimate = data.quote?.estimate;
  // Plain names for the lane + who ships it (was a raw "ROW" chip and "Engine: shiphero").
  const LANE = { AU: 'Australia', NZ: 'New Zealand', US: 'United States', ROW: 'Rest of world' };
  const CARRIER = { FLOWM8: 'FlowM8 (AusPost / DHL)', SHIPHERO: 'ShipHero', FLAVORCLOUD: 'Flavorcloud', NONE: null };

  // Blank ≠ zero: confirming with an empty box would charge no freight silently.
  const costBlank = String(cost).trim() === '';
  const isFreeShipping = !costBlank && Number(cost) === 0;

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Shipping</h3>
        <div className="flex items-center gap-1.5">
          {data.lane && <span className="rounded bg-surface-raised px-1.5 py-0.5 text-[10px] font-medium text-text-secondary">{LANE[data.lane] || data.lane}</span>}
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
        </div>
      </div>

      {/* Returned by confirmShipping when a payment was already recorded, so the schedule could not be
          rebased to the new total. Also written to the order timeline so it isn't lost on reload. */}
      {data.paymentWarning && (
        <p className="mb-2 rounded-md border border-warning/40 bg-warning-light px-2.5 py-2 text-[11px] text-text-primary">{data.paymentWarning}</p>
      )}

      <dl className="mb-2 grid grid-cols-2 gap-y-1 text-xs">
        {CARRIER[data.engine] && (
          <>
            <dt className="text-text-muted">Ships with</dt>
            <dd className="text-right text-text-primary">{CARRIER[data.engine]}</dd>
          </>
        )}
        <dt className="text-text-muted">Shipping{data.treatment === 'FREE' ? ' (free)' : ''}</dt>
        <dd className="text-right text-text-primary">{fmtMoney(data.shippingCost, data.currency)}</dd>
        {isDDP && (
          <>
            <dt className="text-text-muted">Duties</dt>
            <dd className="text-right text-text-primary">{data.dutiesAmount != null ? fmtMoney(data.dutiesAmount, data.currency) : '—'}</dd>
            <dt className="text-text-muted">Taxes</dt>
            <dd className="text-right text-text-primary">{data.taxesAmount != null ? fmtMoney(data.taxesAmount, data.currency) : '—'}</dd>
          </>
        )}
        <dt className="font-medium text-text-primary">Order total</dt>
        <dd className="text-right font-medium text-text-primary">{fmtMoney(data.payableTotal, data.currency)}</dd>
        {data.actualShippingCost != null && (
          <>
            <dt className="text-text-muted">Actual cost (label)</dt>
            <dd className="text-right text-text-primary">{fmtMoney(data.actualShippingCost, data.currency)}</dd>
          </>
        )}
      </dl>
      {estimate && <p className="mb-2 rounded bg-surface-raised/60 px-2 py-1 text-[11px] text-text-muted">Estimate — {data.quote?.note || 'confirm it below; the actual cost is pulled in after shipping.'}</p>}

      {/* Destination + incoterm */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-text-secondary">
          Ship-to country
          <input
            aria-label="Ship-to country code"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase().slice(0, 3))}
            placeholder="AU"
            className="w-14 rounded border border-line px-1.5 py-1 text-xs uppercase"
          />
        </label>
        <button onClick={() => run('dest', () => setDestination(orderId, country))} disabled={busy === 'dest' || !country} className="rounded border border-line px-2 py-1 text-xs text-text-secondary hover:bg-surface-raised disabled:opacity-50">
          Update
        </button>
      </div>
      {data.dutiesOnArrival && (
        <p className="mb-2 rounded bg-warning-light px-2 py-1 text-[11px] font-medium text-warning">
          Import duties &amp; taxes are charged to the customer on arrival — they’re not in the order total.
        </p>
      )}

      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => run('quote', () => quoteShipping(orderId))} disabled={busy === 'quote'} className="rounded-md border border-line px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
          {busy === 'quote' ? 'Quoting…' : 'Get quote'}
        </button>
        {data.store === 'B' && (
          <button onClick={() => run('recon', () => reconcileShipping(orderId).then(() => fetchOrderShipping(orderId)))} disabled={busy === 'recon'} className="rounded-md border border-line px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
            {busy === 'recon' ? '…' : 'Get actual cost from ShipHero'}
          </button>
        )}
      </div>

      {/* Confirm / override */}
      <div className="mt-2 rounded-md border border-line bg-surface-raised/40 p-2">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-text-secondary">
            Shipping
            <input type="number" min={0} value={cost} onChange={(e) => setCost(e.target.value)} className="mt-0.5 block w-20 rounded border border-line px-1.5 py-1 text-xs" />
          </label>
          {isDDP && (
            <>
              <label className="text-xs text-text-secondary">
                Duties
                <input type="number" min={0} value={duties} onChange={(e) => setDuties(e.target.value)} className="mt-0.5 block w-20 rounded border border-line px-1.5 py-1 text-xs" />
              </label>
              <label className="text-xs text-text-secondary">
                Taxes
                <input type="number" min={0} value={taxes} onChange={(e) => setTaxes(e.target.value)} className="mt-0.5 block w-20 rounded border border-line px-1.5 py-1 text-xs" />
              </label>
            </>
          )}
          <button
            onClick={() =>
              run('confirm', () =>
                confirmShipping(orderId, {
                  shippingCost: Number(cost) || 0,
                  treatment: Number(cost) > 0 ? 'MANUAL' : 'FREE',
                  ...(isDDP ? { dutiesAmount: Number(duties) || 0, taxesAmount: Number(taxes) || 0 } : {}),
                })
              )
            }
            // A BLANK box used to mean Number('') === 0 → treatment FREE: the order was confirmed with
            // no freight charged, which then unblocked Approve and emailed the invoice. Require an
            // explicit number, and when that number is 0 say so on the button.
            disabled={busy === 'confirm' || costBlank}
            title={costBlank ? 'Enter a shipping cost first — use 0 to confirm free shipping.' : undefined}
            className="rounded-md bg-accent px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
          >
            {busy === 'confirm' ? 'Confirming…' : isFreeShipping ? 'Confirm free shipping' : 'Confirm shipping'}
          </button>
        </div>
        {costBlank ? (
          <p className="mt-1 text-[11px] text-text-muted">Enter a shipping cost to confirm — type 0 for free shipping.</p>
        ) : isFreeShipping ? (
          <p className="mt-1 text-[11px] text-warning">No freight will be charged — the invoice will show no shipping cost.</p>
        ) : null}
      </div>

      {data.trackingNumbers?.length > 0 && (
        <div className="mt-2 text-xs">
          <span className="text-text-muted">Tracking: </span>
          {data.trackingNumbers.map((t, i) => (
            <a key={t} href={data.trackingUrls?.[i] || '#'} target="_blank" rel="noreferrer" className="mr-2 font-medium text-accent hover:underline">
              {t}
            </a>
          ))}
        </div>
      )}

      {error && <p className="mt-2 text-xs text-error">{error}</p>}
    </div>
  );
}
